import type { IFavoriteTrackRepository } from "@/lib/infrastructure/FavoriteTrackRepository";
import type { ResolvedTrackModel } from "@coffitivity/shared";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const STALE_TIME = 1000 * 60 * 60 * 24; // 24 hours

export const useFavoriteTracks = (
	favoriteTrackRepository: IFavoriteTrackRepository,
) => {
	const queryClient = useQueryClient();

	// Parameterized key — supports multi-user, avoids collisions
	const queryKey = (userId: string) => ["FAVORITE_TRACK_IDS", userId];

	// GET
	const useFavorites = (userId?: string) =>
		useQuery({
			queryKey: queryKey(userId!),
			queryFn: () => favoriteTrackRepository.get(),
			staleTime: STALE_TIME,
			enabled: Boolean(userId),
		});

	// ADD/REMOVE are now parameterized mutations
	const useAddFavorite = () =>
		useMutation({
			mutationFn: async ({
				userId,
				trackId,
			}: {
				userId: string;
				trackId: string;
			}) => {
				// Your existing mutation
				const oldFavorites =
					queryClient.getQueryData<string[]>(queryKey(userId)) ?? [];
				const newFavorites = Array.from(new Set([...oldFavorites, trackId]));
				await favoriteTrackRepository.add(trackId);
				return newFavorites;
			},
			// Optimistically update local cache first
			onMutate: async ({ userId, trackId }) => {
				await queryClient.cancelQueries({ queryKey: queryKey(userId) });

				const previousFavorites =
					queryClient.getQueryData<string[]>(queryKey(userId)) ?? [];
				const newFavorites = Array.from(
					new Set([...previousFavorites, trackId]),
				);

				queryClient.setQueryData(queryKey(userId), newFavorites);

				return { previousFavorites };
			},
			onError: (_error, { userId }, context) => {
				// Rollback on failure
				if (context?.previousFavorites) {
					queryClient.setQueryData(queryKey(userId), context.previousFavorites);
				}
			},
			// Always revalidate after finished
			onSettled: (_data, _error, { userId }) => {
				queryClient.invalidateQueries({ queryKey: queryKey(userId) });
				// TODO: need a better way to reference this key
				queryClient.invalidateQueries({ queryKey: ["TRACKS", "FAVORITES"] });
			},
		});

	const useRemoveFavorite = () =>
		useMutation({
			mutationFn: async ({
				userId,
				trackId,
			}: {
				userId: string;
				trackId: string;
			}) => {
				const oldFavorites =
					queryClient.getQueryData<string[]>(queryKey(userId)) ?? [];
				const newFavorites = oldFavorites.filter((id) => id !== trackId);
				await favoriteTrackRepository.remove(trackId);
				return newFavorites;
			},
			onMutate: async ({ userId, trackId }) => {
				await queryClient.cancelQueries({ queryKey: queryKey(userId) });

				const previousFavoriteIds =
					queryClient.getQueryData<string[]>(queryKey(userId)) ?? [];
				const newFavoriteIds = previousFavoriteIds.filter(
					(id) => id !== trackId,
				);

				queryClient.setQueryData(queryKey(userId), newFavoriteIds);

				// NOTE: we have to also clear the cached favorite tracks data otherwise they'll still appear
				// on the home and favorite tracks page
				const previousFavoriteTracks = queryClient.getQueryData<
					ResolvedTrackModel[]
				>(["TRACKS", "FAVORITES"]);
				const newFavoriteTracks = previousFavoriteTracks?.filter(
					(track) => track.id !== trackId,
				);

				queryClient.setQueryData(["TRACKS", "FAVORITES"], newFavoriteTracks);

				return {
					previousFavorites: previousFavoriteIds,
					previousFavoriteTracks,
				};
			},
			onError: (_error, { userId }, context) => {
				if (context?.previousFavorites) {
					queryClient.setQueryData(queryKey(userId), context.previousFavorites);
				}
				if (context?.previousFavoriteTracks) {
					queryClient.setQueryData(
						["TRACKS", "FAVORITES"],
						context.previousFavoriteTracks,
					);
				}
			},
			onSettled: (_data, _error, { userId }) => {
				queryClient.invalidateQueries({ queryKey: queryKey(userId) });
				// TODO: need a better way to reference this key
				queryClient.invalidateQueries({ queryKey: ["TRACKS", "FAVORITES"] });
			},
		});

	// Return them as a handy object for auto-complete and clarity
	return {
		useFavorites,
		useAddFavorite,
		useRemoveFavorite,
	};
};
